import React from "react"; // No need to import { Component } }
import { Card, Divider, Grid, Header, Icon, Table } from "semantic-ui-react";
import "../../node_modules/react-vis/dist/style.css";
import ChartUtils from "../charts/ChartUtils";
import "../css/home.css"; // Ensure you add the CSS classes here
import Utils from "../util/Utils";
import { isMobile } from "react-device-detect";

// Define the configuration for the rows in the main table body
const revenueGrowthRowsConfig = [
  {
    label: "Revenue", // Label for the first cell in the row
    dataKey: "Revenue", // Key used to access data in incomeStmtValues[index]
    valuePath: "value", // Path under dataKey to get the specific value (e.g., incomeStmtValues[index]["Revenue"].value)
    format: Utils.format_currency_short, // Formatting function
    params: [10, 1], // Additional params for Utils.TableCell
    trendChartKey: "Revenue", // Key for getXyDataFromValues for the tiny chart
    trendChartDataKey: "Value", // Data key for the tiny chart
  },
  {
    label: "YoY Growth",
    dataKey: "Revenue",
    valuePath: "yoyGrowth", // Use yoyGrowth property
    format: Utils.format_percent,
    params: [0.1, 1],
    trendChartKey: "Revenue", // Trend chart is based on Revenue data, not the growth percentage itself
    trendChartDataKey: "Growth", // Data key for the tiny chart
  },
  // Add other relevant rows here following the same structure if needed
];

// Helper function to render table headers with mobile conditional logic
const renderRevenueHeaderCells = (incomeStmtValues) => {
  return Object.keys(incomeStmtValues).map((index) => {
    const dataForHeader = incomeStmtValues[index];
    const year = dataForHeader?.["Revenue"]?.year; // Safely access the year

    if (year) {
      const headerLabel = year.substring(0, 4);
      return <Table.HeaderCell key={index}>{headerLabel}</Table.HeaderCell>;
    }

    return null;
  });
};

const SalesGrowthCheck = (props) => {
  const incomeYValues = props.stockValues.incomeStmtValues.slice(
    isMobile ? -3 : -5
  );

  return (
    <>
      <Divider horizontal>
        <Header as="h4">
          <Icon size="large" name="chart line" />
          Is Revenue positive and growing consistently ?
        </Header>
      </Divider>
      <br />

      <Grid stackable centered>
        <Grid.Row columns={{ mobile: 1, tablet: 2, computer: 3 }}>
          <Grid.Column width={5}>
            <div className="chart-container">
              {ChartUtils.renderGrowthCard(
                "Revenue", // Title for the growth card
                ChartUtils.getXyDataFromValues(
                  props.stockValues.incomeStmtValues,
                  "Revenue"
                ),
                Utils.format_number
              )}
            </div>
          </Grid.Column>

          <Grid.Column width={6}>
            <div style={{ padding: "0 0.5rem" }}>
              <Card fluid raised>
                <Card.Content>
                  <Card.Header textAlign="center">
                    Past Years Values
                    <Header.Subheader style={{ marginTop: "0.25em" }}>
                      (10 to 15% growth is ideal)
                    </Header.Subheader>
                  </Card.Header>
                  <Card.Description>
                    <Table
                      size="small"
                      unstackable
                      compact="very"
                      celled
                      className="table-margin-auto" // Use CSS class for margin
                    >
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Year</Table.HeaderCell>
                          {renderRevenueHeaderCells(incomeYValues)}
                          <Table.HeaderCell>Trend</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {revenueGrowthRowsConfig.map((rowConfig) => (
                          <Table.Row key={rowConfig.label}>
                            <Table.Cell>{rowConfig.label}</Table.Cell>
                            {renderDataCells(
                              incomeYValues, // Pass the full data object
                              rowConfig.dataKey, // Pass the data key (e.g., "Revenue")
                              rowConfig.format, // Pass the format function
                              rowConfig.valuePath, // Pass the path to the specific value (.value or .yoyGrowth)
                              rowConfig.params // Pass additional params for Utils.TableCell
                            )}
                            {ChartUtils.renderTinyChart(
                              ChartUtils.getXyDataFromValues(
                                incomeYValues,
                                rowConfig.trendChartKey // Use the trend chart key from config
                              ),
                              rowConfig.trendChartDataKey // Use the trend chart data key from config
                            )}
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </Card.Description>
                </Card.Content>
                <Card.Content extra>
                  An increasing revenue is the sign of a good company. It
                  denotes that the demand for the company’s products or services
                  has increased. Conversely, a decreasing or stagnant revenue is
                  a bad sign.
                </Card.Content>
              </Card>
            </div>
          </Grid.Column>

          <Grid.Column width={5}>
            <div className="chart-container">
              {ChartUtils.renderObservations(
                props.title, // Assuming props.title is the stock ticker or name
                props.stockValues.revenueObservations
              )}
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </>
  );
};

const renderDataCells = (
  incomeStmtValues,
  dataKey,
  format_function,
  valuePath,
  params
) => {
  return Object.keys(incomeStmtValues).map((index) => {
    const cellData = incomeStmtValues[index]?.[dataKey]?.[valuePath];

    const cellElement = Utils.TableCell(
      cellData, // Pass the actual value
      format_function,
      ...params // Spread the params array
    );

    return React.cloneElement(cellElement, { key: index });
  });
};

export default SalesGrowthCheck;
